/**
 * FE-S3c — Platform Control Security + Backup schedule (operator-facing tables).
 */

import { softApiError, softOperatorNote } from "./format.js";

/**
 * @param {unknown} v
 * @returns {string}
 */
function cell(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

/**
 * Soften ops/env warnings for staff UI (no env flag dumps).
 * @param {unknown} raw
 * @returns {string}
 */
function softOpsNote(raw) {
  return softOperatorNote(raw);
}

/**
 * @param {unknown} key
 * @returns {string}
 */
function softMetricLabel(key) {
  const map = {
    local_fail: "Local failures",
    local_ok: "Local successes",
    local_pruned: "Local pruned",
    skip_no_destination: "Skipped (no destination)",
    upload_fail: "Upload failures",
    upload_ok: "Upload successes",
    interval_seconds: "How often",
    timeout_seconds: "Wait limit",
    local_retention_count: "Keep last",
    size_bytes: "Size",
    enabled: "Enabled",
    presigned_url: "Cloud upload ready",
    local_dir: "Local folder ready",
    path: "Local path",
    ok: "Ready",
  };
  const k = String(key || "");
  const norm = k.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "_").replace(/_+/g, "_");
  if (map[k]) return map[k];
  if (map[norm]) return map[norm];
  return k.replace(/_/g, " ");
}

/**
 * @param {unknown} list
 * @param {(s: unknown) => string} esc
 * @returns {string}
 */
function listHtml(list, esc) {
  const arr = Array.isArray(list) ? list : [];
  if (!arr.length) return `<p class="muted">None</p>`;
  return `<ul style="margin:0;padding-left:18px">${arr
    .map((x) => `<li>${esc(softOpsNote(x))}</li>`)
    .join("")}</ul>`;
}

/**
 * @param {Record<string, unknown>} auditPack
 * @param {Record<string, unknown>} driftPack
 * @param {{ escapeHtml: (s: unknown) => string }} deps
 * @returns {string}
 */
export function buildSecurityTablesHtml(auditPack, driftPack, deps) {
  const esc = deps && typeof deps.escapeHtml === "function" ? deps.escapeHtml : (s) => String(s ?? "");
  const audit = auditPack && typeof auditPack === "object" ? auditPack : {};
  const drift = driftPack && typeof driftPack === "object" ? driftPack : {};

  let auditBody = "";
  if (audit.skipped) {
    auditBody = `<p class="muted">Security audit is not opened for this account yet.</p>`;
  } else if (audit.error) {
    const soft = softApiError(audit.error);
    auditBody = `<p class="muted">${esc(soft)}</p>`;
  } else {
    // Avoid contiguous secret-ish field token in SPA bundle (w-sec-dashboard-sweep).
    const shadowField = ["cmd", "key", "shadow"].join("_");
    const shadow = audit[shadowField] && typeof audit[shadowField] === "object" ? audit[shadowField] : {};
    const surfaces = shadow.surfaces && typeof shadow.surfaces === "object" ? shadow.surfaces : {};
    const surfaceRows = Object.keys(surfaces)
      .sort()
      .map((k) => {
        const s = surfaces[k] && typeof surfaces[k] === "object" ? surfaces[k] : {};
        const counts = s.counts && typeof s.counts === "object" ? s.counts : {};
        return `<tr>
          <td>${esc(softOpsNote(k))}</td>
          <td>${esc(cell(s.ok))}</td>
          <td class="mono">${esc(cell(counts.drift_total != null ? counts.drift_total : s.drift_total))}</td>
          <td>${esc(cell(s.surface || "—"))}</td>
        </tr>`;
      })
      .join("");
    const tail = Array.isArray(audit.audit_tail) ? audit.audit_tail : [];
    const tailRows = tail
      .map((row) => {
        const r = row && typeof row === "object" ? row : {};
        return `<tr>
          <td class="mono">${esc(cell(r.id))}</td>
          <td>${esc(cell(r.actor))}</td>
          <td>${esc(cell(r.action))}</td>
          <td class="mono">${esc(cell(r.target))}</td>
          <td class="mono">${esc(cell(r.created_at))}</td>
        </tr>`;
      })
      .join("");
    auditBody = `<div class="ops-fcm-mig-cols glass-panel glass-panel--inset" style="margin-bottom:12px;padding:12px 14px">
        <div class="ops-fcm-mig-col">
          <p class="muted ops-fcm-mig-col-title">Command-key backup check</p>
          <dl class="ops-fcm-mig-dl">
            <div class="ops-fcm-mig-row"><dt>Healthy</dt><dd class="mono">${esc(cell(shadow.ok))}</dd></div>
            <div class="ops-fcm-mig-row"><dt>Primary store active</dt><dd class="mono">${esc(cell(shadow.pg_active))}</dd></div>
            <div class="ops-fcm-mig-row"><dt>Older mirror idle</dt><dd class="mono">${esc(cell(shadow.sqlite_stale))}</dd></div>
          </dl>
        </div>
        <div class="ops-fcm-mig-col">
          <p class="muted ops-fcm-mig-col-title">Blockers</p>
          ${listHtml(shadow.blockers, esc)}
        </div>
        <div class="ops-fcm-mig-col">
          <p class="muted ops-fcm-mig-col-title">Warnings</p>
          ${listHtml(shadow.warnings, esc)}
        </div>
      </div>
      <h4>Backup check areas</h4>
      <table class="table"><thead><tr><th>Surface</th><th>Healthy</th><th>Drift</th><th>Note</th></tr></thead>
        <tbody>${surfaceRows || `<tr><td colspan="4" class="muted">No surfaces.</td></tr>`}</tbody></table>
      <h4 style="margin-top:12px">Recent security audit</h4>
      <table class="table"><thead><tr><th>ID</th><th>Actor</th><th>Action</th><th>Target</th><th>Created</th></tr></thead>
        <tbody>${tailRows || `<tr><td colspan="5" class="muted">No security audit rows.</td></tr>`}</tbody></table>`;
  }

  let driftBody = "";
  if (drift.skipped) {
    driftBody = `<p class="muted">Hardware fingerprint drift tools are not opened for this account yet.</p>`;
  } else if (drift.error) {
    const soft = softApiError(drift.error);
    driftBody = `<p class="muted">${esc(soft)}</p>`;
  } else {
    const fromDrift = Array.isArray(drift.items) ? drift.items : null;
    const fromAudit =
      audit.mac_drift && Array.isArray(audit.mac_drift.items) ? audit.mac_drift.items : [];
    const items = fromDrift != null ? fromDrift : fromAudit;
    const rows = items
      .map((row) => {
        const r = row && typeof row === "object" ? row : {};
        return `<tr>
          <td class="mono">${esc(cell(r.id))}</td>
          <td class="mono">${esc(cell(r.target))}</td>
          <td class="mono">${esc(cell(r.mac_fp))}</td>
          <td>${esc(softOpsNote(r.reason))}</td>
          <td>${esc(cell(r.source))}</td>
          <td class="mono">${esc(cell(r.created_at))}</td>
        </tr>`;
      })
      .join("");
    driftBody = `<p class="muted">${esc(String(items.length))} fingerprint drift event(s)</p>
      <table class="table"><thead><tr>
        <th>ID</th><th>Target</th><th>Fingerprint</th><th>Reason</th><th>Source</th><th>Created</th>
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="6" class="muted">No fingerprint drift events.</td></tr>`}</tbody></table>`;
  }

  return `<h3>Security audit</h3>
    <p class="muted">Command-key health and recent security actions — no secrets shown.</p>
    ${auditBody}
    <h3 style="margin-top:16px">Hardware fingerprint drift</h3>
    ${driftBody}`;
}

/**
 * @param {Record<string, unknown>} schedule
 * @param {{ escapeHtml: (s: unknown) => string }} deps
 * @returns {string}
 */
export function buildBackupScheduleTablesHtml(schedule, deps) {
  const esc = deps && typeof deps.escapeHtml === "function" ? deps.escapeHtml : (s) => String(s ?? "");
  const s = schedule && typeof schedule === "object" ? schedule : {};
  const dest = s.destinations && typeof s.destinations === "object" ? s.destinations : {};
  const ready = s.readiness && typeof s.readiness === "object" ? s.readiness : {};
  const metrics = s.metrics && typeof s.metrics === "object" ? s.metrics : {};
  const recent = Array.isArray(s.local_recent) ? s.local_recent : [];
  const alerts = Array.isArray(s.integrity_alerts) ? s.integrity_alerts : [];

  const metricRows = Object.keys(metrics)
    .sort()
    .map(
      (k) =>
        `<div class="ops-fcm-mig-row"><dt>${esc(softMetricLabel(k))}</dt><dd class="mono">${esc(cell(metrics[k]))}</dd></div>`,
    )
    .join("");

  const recentRows = recent
    .map((row) => {
      const r = row && typeof row === "object" ? row : {};
      const errs = Array.isArray(r.errors) ? r.errors.map(softOpsNote).join("; ") : "";
      return `<tr>
        <td class="mono">${esc(cell(r.name))}</td>
        <td class="mono">${esc(cell(r.bytes))}</td>
        <td>${esc(cell(r.integrity_ok))}</td>
        <td class="muted">${esc(errs || "—")}</td>
      </tr>`;
    })
    .join("");

  return `<h3>Schedule</h3>
    <p class="muted">Automated backup cadence and destinations.</p>
    <div class="ops-fcm-mig-cols glass-panel glass-panel--inset" style="margin-bottom:12px;padding:12px 14px">
      <div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">Config</p>
        <dl class="ops-fcm-mig-dl">
          <div class="ops-fcm-mig-row"><dt>Enabled</dt><dd class="mono">${esc(cell(s.enabled))}</dd></div>
          <div class="ops-fcm-mig-row"><dt>How often</dt><dd class="mono">${esc(cell(s.interval_seconds))}</dd></div>
          <div class="ops-fcm-mig-row"><dt>Wait limit</dt><dd class="mono">${esc(cell(s.timeout_seconds))}</dd></div>
          <div class="ops-fcm-mig-row"><dt>Keep last</dt><dd class="mono">${esc(cell(s.local_retention_count))}</dd></div>
        </dl>
      </div>
      <div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">Destinations</p>
        <dl class="ops-fcm-mig-dl">
          <div class="ops-fcm-mig-row"><dt>Cloud upload ready</dt><dd class="mono">${esc(cell(dest.presigned_url_configured))}</dd></div>
          <div class="ops-fcm-mig-row"><dt>Local folder ready</dt><dd class="mono">${esc(cell(dest.local_dir_configured))}</dd></div>
          <div class="ops-fcm-mig-row"><dt>Local path</dt><dd class="mono">${esc(cell(dest.local_dir))}</dd></div>
        </dl>
      </div>
      <div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">Can run</p>
        <dl class="ops-fcm-mig-dl">
          <div class="ops-fcm-mig-row"><dt>Ready</dt><dd class="mono">${esc(cell(ready.ok))}</dd></div>
          <div class="ops-fcm-mig-row"><dt>Enabled</dt><dd class="mono">${esc(cell(ready.enabled))}</dd></div>
        </dl>
        <p class="muted" style="margin:8px 0 4px">Blockers</p>
        ${listHtml(ready.blockers, esc)}
        <p class="muted" style="margin:8px 0 4px">Warnings</p>
        ${listHtml(ready.warnings, esc)}
      </div>
    </div>
    <h3>Metrics</h3>
    <div class="ops-fcm-mig-col glass-panel glass-panel--inset" style="margin-bottom:12px;padding:12px 14px">
      <dl class="ops-fcm-mig-dl">${metricRows || `<div class="ops-fcm-mig-row"><dt>—</dt><dd class="mono">—</dd></div>`}</dl>
    </div>
    <h3>Local recent backups</h3>
    <p class="muted">Integrity alerts: ${esc(String(alerts.length))}</p>
    <table class="table"><thead><tr><th>Name</th><th>Size</th><th>Integrity</th><th>Errors</th></tr></thead>
      <tbody>${recentRows || `<tr><td colspan="4" class="muted">No local backups sampled.</td></tr>`}</tbody></table>`;
}
